import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import * as FlatStates from '../../internal';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const ANNOTATION_GROUP_ENTITY_TOKEN = new StateToken<AnnotationGroupEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_GROUP_ENTITY);

export interface AnnotationGroupEntityStateModel extends FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.GroupEntityModels.Group> {
  groupId: string | null;
  areaGroupId: string | null;
}

@State<AnnotationGroupEntityStateModel>({
  name: ANNOTATION_GROUP_ENTITY_TOKEN,
  defaults: {
    groupId: null,
    areaGroupId: null,
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class AnnotationGroupEntityState extends HistoryEntityBaseState<FlatModels.GroupEntityModels.Group, AnnotationGroupEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([AnnotationGroupEntityState])
  static groupId$(state: AnnotationGroupEntityStateModel): string | null {
    return state.groupId;
  }

  @Selector([AnnotationGroupEntityState])
  static areaGroupId$(state: AnnotationGroupEntityStateModel): string | null {
    return state.areaGroupId;
  }

  @Selector([AnnotationGroupEntityState])
  static override typedEntities$(state: AnnotationGroupEntityStateModel): { [id: string]: FlatModels.GroupEntityModels.Group } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([AnnotationGroupEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.GroupEntityModels.Group }): FlatModels.GroupEntityModels.Group[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([AnnotationGroupEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.GroupEntityModels.Group }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([AnnotationGroupEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.GroupEntityModels.Group }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  @Selector([
    AnnotationGroupEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedSlidePage$
  ])
  static currentSelectedSlideGroups$(typedEntities: { [id: string]: FlatModels.GroupEntityModels.Group },
                                     currentSelectedSlidePage: FlatModels.PageEntityModels.Slide | null) {
    // ATTENTION: Group Description Comes From First Annotation
    if (currentSelectedSlidePage && currentSelectedSlidePage.groups) {
      return currentSelectedSlidePage.groups.reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as FlatModels.GroupEntityModels.Group[]);
    }
    return [];
  }
  
  @Selector([AnnotationGroupEntityState.currentSelectedSlideGroups$])
  static isAnnotationInCurrentSelectedSlideGroups$(currentSelectedSlideGroups: FlatModels.GroupEntityModels.Group[]) {
    return (annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) => {
      for (const group of currentSelectedSlideGroups) {
        if (group.annotations.includes(annotation.id)) {
          return true;
        }
      }
      return false;
    }
  }
  
  @Selector([AnnotationGroupEntityState.currentSelectedSlideGroups$])
  static slideGroupsArray$(currentSelectedSlideGroups: FlatModels.GroupEntityModels.Group[]) {
    return currentSelectedSlideGroups.reduce((arr, group) => {
      arr.push({
        value: 'group-' + group.id,
        name: group.name
      });
      return arr;
    }, [] as { value: string, name: string }[]);
  }
  
  @Selector([AnnotationGroupEntityState.currentSelectedSlideGroups$])
  static currentSelectedSlideGroupsArray$(currentSelectedSlideGroups: FlatModels.GroupEntityModels.Group[]) {
    return currentSelectedSlideGroups.reduce((arr, group) => {
      arr.push({
        value: group.id,
        name: group.name
      });
      return arr;
    }, [] as {value: string, name: string}[]);
  }
  
  @Selector([AnnotationGroupEntityState.currentSelectedSlideGroups$])
  static groupsByAnnotation$(groups: FlatModels.GroupEntityModels.Group[]) {
    return (id: string) => {
      return groups.reduce((arr, group) => {
        if (group.annotations.includes(id)) {
          arr.push(group);
        }
        return arr;
      }, [] as FlatModels.GroupEntityModels.Group[]);
    }
  }
  
  @Selector([AnnotationGroupEntityState.currentSelectedSlideGroups$])
  static groupIdsByAnnotation$(groups: FlatModels.GroupEntityModels.Group[]) {
    return (id: string) => {
      return groups.reduce((arr, group) => {
        if (group.annotations.includes(id)) {
          arr.push(group.id);
        }
        return arr;
      }, [] as string[]);
    }
  }

  @Selector([
    FlatStates.NodeEntityState.currentSelectedSlideNode$,
    FlatStates.AnnotationEntityState.currentSelectedSlideImageAnnotations$,
    FlatStates.AnnotationLabelEntityState.entities$,
    AnnotationGroupEntityState.entities$
  ])
  static currentSelectedSlideImageAreaGroups$(currentSelectedSlideNode: FlatModels.NodeEntityModels.NodeEntityType,
                                              currentSelectedSlideImageAnnotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[],
                                              annotationLabels: FlatModels.AnnotationLabelEntityModels.AnnotationLabel[],
                                              groups: FlatModels.GroupEntityModels.Group[]) {
    // Get Interactive Areas
    const interactiveAreas = currentSelectedSlideImageAnnotations.filter(a => a.type === FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm
      && (a as FlatModels.AnnotationEntityModels.FreeForm).shape === FlatModels.AnnotationEntityModels.Shape.enum.interactiveArea);

    // Check Interactive Areas
    if (interactiveAreas.length > 0) {
      // Temporary Groups
      const temporaryGroupsMap = new Map<string, FlatModels.GroupEntityModels.Group>();

      interactiveAreas.forEach(ia => {
        const label = annotationLabels.find(al => al.id === ia.label as unknown as string);
        if ((ia.groups?.length || 0) === 0) {
          const group: FlatModels.GroupEntityModels.Group = {
            id: ia.id,
            annotations: [ia.id],
            name: label?.text || '',
            description: label?.description || '',
            order: temporaryGroupsMap.size,
            color: null,
            slide: currentSelectedSlideNode.page,
          };
          temporaryGroupsMap.set(group.id, group);
        } else {
          ia.groups?.forEach((g) => {
            const groupId = g as unknown as string;
            const tempGroup = temporaryGroupsMap.get(groupId);
            if (tempGroup) {
              (tempGroup.annotations as unknown as string[]).push(ia.id);
              if ((tempGroup.description?.length || 0) < (label?.description?.length || 0)) {
                tempGroup.description = label?.description || '';
              }
              temporaryGroupsMap.set(groupId, tempGroup);
            } else {
              const realGroup = groups.find(g => g.id === groupId);
              const group: FlatModels.GroupEntityModels.Group = {
                id: ia.id,
                annotations: [ia.id],
                name: realGroup?.name || label?.text || '',
                description: label?.description || '',
                order: temporaryGroupsMap.size,
                color: null,
                slide: currentSelectedSlideNode.page,
              };
              temporaryGroupsMap.set(groupId, group);
            }
          })
        }
      });

      const dedupMap = new Map<string, FlatModels.GroupEntityModels.Group>();
      Array.from(temporaryGroupsMap).forEach((pair) => {
        pair[1].annotations?.sort();
        const ukey = pair[1].annotations?.join(',') || pair[0];
        dedupMap.set(ukey, pair[1]);
      })

      return Array.from(dedupMap).map(pair => pair[1]);
    }

    return [];
  }

  @Selector([
    AnnotationGroupEntityState.currentSelectedSlideImageAreaGroups$,
  ])
  static currentSelectedSlideImageAreaGroupsMap$(areaGroups: FlatModels.GroupEntityModels.Group[]) {
    const map = new Map<string, FlatModels.GroupEntityModels.Group>();
    areaGroups.forEach((areaGroup) => {
      areaGroup.annotations?.forEach((annotationId) => {
        map.set(annotationId as unknown as string, areaGroup);
      })
    })
    return map;
  }

  @Selector([
    AnnotationGroupEntityState.currentSelectedSlideGroups$,
    AnnotationGroupEntityState.groupId$
  ])
  static currentSelectedSlideGroup$(currentSelectedSlideGroups: FlatModels.GroupEntityModels.Group[],
                                    groupId: string | null) {
    return groupId !== null && Array.isArray(currentSelectedSlideGroups) ? currentSelectedSlideGroups.find(sg => sg.id === groupId) || null : null;
  }
  
  @Selector([AnnotationGroupEntityState.currentSelectedSlideGroup$])
  static currentSelectedSlideGroupAnnotationOptions$(currentSelectedSlideGroup: FlatModels.GroupEntityModels.Group) {
    return (annotationOptionArray: { value: string, name: string}[],
            group?: FlatModels.GroupEntityModels.Group) => {
      if (!group) {
        group = currentSelectedSlideGroup;
      }
      return annotationOptionArray.reduce((arr, annotationOption) => {
        if (group && !(group.annotations || []).includes(annotationOption.value)) {
          arr.push({
            value: annotationOption.value,
            name: annotationOption.name
          });
        }
        return arr;
      }, [] as { value: string, name: string }[]);
    };
  }

  @Selector([
    AnnotationGroupEntityState.currentSelectedSlideImageAreaGroupsMap$,
    AnnotationGroupEntityState.areaGroupId$
  ])
  static currentSelectedSlideImageAreaGroup$(currentSelectedSlideAreaGroupsMap: Map<string, FlatModels.GroupEntityModels.Group>,
                                             areaGroupId: string | null) {
    return areaGroupId !== null ? (currentSelectedSlideAreaGroupsMap?.get(areaGroupId) || null) : null;
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.AnnotationGroupEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<AnnotationGroupEntityStateModel>,
                            action: FlatActions.AnnotationGroupEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationGroupEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<AnnotationGroupEntityStateModel>,
                              action: FlatActions.AnnotationGroupEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.AnnotationGroupEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<AnnotationGroupEntityStateModel>,
                               action: FlatActions.AnnotationGroupEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationGroupEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<AnnotationGroupEntityStateModel>,
                                 action: FlatActions.AnnotationGroupEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.AnnotationGroupEntityActions.AddEntity)
  @Undoable(
    FlatActions.AnnotationGroupEntityActions.AddEntity,
    ANNOTATION_GROUP_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<AnnotationGroupEntityStateModel>,
                     action: FlatActions.AnnotationGroupEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.AnnotationGroupEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.AnnotationGroupEntityActions.UpdateEntity,
    ANNOTATION_GROUP_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<AnnotationGroupEntityStateModel>,
                        action: FlatActions.AnnotationGroupEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.AnnotationGroupEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.AnnotationGroupEntityActions.RemoveEntity,
    ANNOTATION_GROUP_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<AnnotationGroupEntityStateModel>,
                        action: FlatActions.AnnotationGroupEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.AnnotationGroupEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<AnnotationGroupEntityStateModel>,
                        action: FlatActions.AnnotationGroupEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationGroupEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<AnnotationGroupEntityStateModel>,
                        action: FlatActions.AnnotationGroupEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationGroupEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<AnnotationGroupEntityStateModel>,
                            action: FlatActions.AnnotationGroupEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  @Action(FlatActions.AnnotationGroupEntityActions.SetGroupId)
  setGroupId({setState}: StateContext<AnnotationGroupEntityStateModel>,
             {groupId}: FlatActions.AnnotationGroupEntityActions.SetGroupId) {
    setState(
      patch({
        groupId
      })
    );
  }

  @Action(FlatActions.AnnotationGroupEntityActions.SetAreaGroupId)
  setAreaGroupId({setState}: StateContext<AnnotationGroupEntityStateModel>,
                 {areaGroupId}: FlatActions.AnnotationGroupEntityActions.SetAreaGroupId) {
    setState(
      patch({
        areaGroupId
      })
    );
  }

  @Action(FlatActions.AnnotationGroupEntityActions.RearrangeGroups)
  rearrangeGroups({getState, dispatch}: StateContext<AnnotationGroupEntityStateModel>,
                  {groups}: FlatActions.AnnotationGroupEntityActions.RearrangeGroups) {
    // Get Entities
    const entities = structuredClone(getState().entities);
    
    // Iterate Through Groups
    for (let i = 0; i < groups.length; i++) {
      // Get Group
      const group = groups[i];
      
      // Get Entity
      let entity;
      if (typeof group === 'string') {
        entity = entities[group];
      } else {
        entity = entities[group.id];
      }
      
      // Update Entity
      entity.order = i;
      
      // Dispatch Update
      dispatch(new FlatActions.AnnotationGroupEntityActions.UpdateEntity(entity));
    }
  }

  //</editor-fold>
}
